import { orchestrator } from 'satcheljs';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import {
    getComposeEvent,
    getOriginalEvent,
    getFullComposeViewState,
} from 'owa-calendar-compose-store-manager';
import getCalendarOwnerFromItem from 'owa-calendar-forms-common/lib/utils/getCalendarOwnerFromItem';
import { createEnhancedLocationFromRoomInfo, isRoom } from 'owa-location-persona';
import { getUserConfiguration } from 'owa-session-store';
import {
    setShowSchedulingAssistant,
    setShowDateSelectorTimeZones,
    logComposeFormOpenedTimes,
    updateDateTimes,
    updateRequiredAttendees,
    updateOptionalAttendees,
    updateLocations,
    updateAllDay,
    openComposeSchedulingAssistant,
    handleSchedulingAssistantSave,
    handleSchedulingAssistantDiscard,
    closeComposeSchedulingAssistant,
} from 'owa-calendar-compose-form-actions';
import {
    lazyInitializeSchedulingAssistant,
    lazyResetSchedulingAssistant,
} from 'owa-calendar-scheduling-assistant';
import { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import { logChangeTimeActions } from 'owa-calendar-compose-form-actions/lib/actions/eventMetricsActions';
import { isFTDFSeriesMaster } from 'owa-calendar-event-capabilities';
import type { OwaDate } from 'owa-datetime';
import resolveStringLocation from '../utils/resolveStringLocation';
import { updateCollabSpace } from '../utils/updateCollabSpace';
import { isFeatureEnabled } from 'owa-feature-flags';

orchestrator(openComposeSchedulingAssistant, async actionMessage => {
    const { formId } = actionMessage;

    const event = getComposeEvent(formId);
    const fullComposeViewState = getFullComposeViewState(formId);

    const { originalStart, originalEnd, originalIsAllDayEvent } = getOriginalEventTime(
        actionMessage.formId
    );

    await lazyInitializeSchedulingAssistant.importAndExecute(
        event.Start,
        event.End,
        event.IsAllDayEvent,
        originalStart,
        originalEnd,
        originalIsAllDayEvent,
        event.RequiredAttendees,
        event.OptionalAttendees,
        event.Resources,
        {
            // For new calendar items, Organizer might not be set yet,
            Mailbox: event.Organizer?.Mailbox || getCalendarOwnerFromItem(event),
            ResponseType: 'Organizer',
        },
        event.IsOrganizer,
        false /* isProposeNewTime */,
        fullComposeViewState.isAcceptProposeNewTime,
        !isFTDFSeriesMaster(event) && event.IsOrganizer /* canModifyAttendees */,
        getComposeCalendarColorScheme(formId),
        event.Recurrence,
        event.ParentFolderId,
        fullComposeViewState.shouldAdjustStartTimeEndTime,
        fullComposeViewState.isAllDayEventExplicitlySet
    );

    resolveStringLocation(fullComposeViewState.unresolvedLocation);

    logComposeFormOpenedTimes();

    // Update the calendar event to set the collaborative space from the collaborative space store
    if (isFeatureEnabled('cal-cmp-fluidCollaborativeSpace')) {
        await updateCollabSpace(event, fullComposeViewState?.collabSpaceViewStateId);
    }

    setShowSchedulingAssistant(formId, true);
});

orchestrator(handleSchedulingAssistantSave, actionMessage => {
    const { formId, finalState, isAllDayEventExplicitlySet } = actionMessage;

    const event = getComposeEvent(formId);

    const userTimeZone = getUserConfiguration().UserOptions.TimeZone;
    updateAllDay(finalState.IsAllDayEvent, isAllDayEventExplicitlySet);
    updateDateTimes(
        {
            start: finalState.Start,
            end: finalState.End,
        },
        TimeChangeActionType.SchedulingAssistantSaveChanges
    );

    setShowDateSelectorTimeZones(
        !finalState.IsAllDayEvent &&
            (finalState.Start.tz != userTimeZone || finalState.End.tz != userTimeZone)
    );

    /* WI 102453. We will re add this code once the OCS setting is ready.
        if (
            isFeatureEnabled('cal-cmp-fluidCollaborativeSpace') &&
            shouldAddCollabSpace(event, finalState)
        ) {
            const fullComposeViewState = getFullComposeViewState(formId);

            updateCollabSpaceOnRecipientsChanged(
                event,
                fullComposeViewState.collabSpaceViewStateId,
                formId
            );
        }
        */

    updateRequiredAttendees(finalState.RequiredAttendees);
    updateOptionalAttendees(finalState.OptionalAttendees);

    // Location well
    // filter rooms from locations and add the locations from SA
    let locations = event.Locations.filter(location => !isRoom(location.PostalAddress));

    finalState.Resources.forEach(value =>
        locations.push(
            createEnhancedLocationFromRoomInfo(value.Mailbox.Name, value.Mailbox.EmailAddress)
        )
    );

    updateLocations(locations);

    logComposeFormOpenedTimes();

    closeComposeSchedulingAssistant(formId);
});

orchestrator(handleSchedulingAssistantDiscard, actionMessage => {
    const { formId } = actionMessage;

    logChangeTimeActions(TimeChangeActionType.SchedulingAssistantDiscardChanges);

    logComposeFormOpenedTimes();

    closeComposeSchedulingAssistant(formId);
});

orchestrator(closeComposeSchedulingAssistant, actionMessage => {
    const { formId } = actionMessage;

    lazyResetSchedulingAssistant.importAndExecute();

    if (getFullComposeViewState(formId)) {
        setShowSchedulingAssistant(formId, false);
    }
});

function getOriginalEventTime(
    formId: string | null
): {
    originalStart: OwaDate;
    originalEnd: OwaDate;
    originalIsAllDayEvent: boolean;
} {
    const originalEvent = getOriginalEvent(formId);

    return {
        originalStart: originalEvent.Start,
        originalEnd: originalEvent.End,
        originalIsAllDayEvent: originalEvent.IsAllDayEvent,
    };
}

/* WI 102453. We will re add this code once the OCS setting is ready.
function shouldAddCollabSpace(event: CalendarEvent, finalState: CalendarEventData) {
    const attendeesInCompose = event.RequiredAttendees.length + event.OptionalAttendees.length;
    const attendeesInSchedAsst =
        finalState.RequiredAttendees.length + finalState.OptionalAttendees.length;
    return attendeesInCompose === 0 && attendeesInSchedAsst > 0;
}
*/
